import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert, Badge } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async"; // Para búsqueda asíncrona
import TopHeader from "../../common/Header/TopHeader";
import {
  createLabTemplate,
  updateLabTemplate,
  getLabTemplateCategories,
} from "../services/labTestTemplateService";
import {
  searchLoincCodes,
  getLoincCodeByNumber,
} from "../services/loincService"; // Importar servicios LOINC

const LaboratoryTemplateAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const templateData = location.state?.templateData;
  const isEditMode = !!templateData;

  // Estados principales
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    loincCode: "",
    category: "",
    description: "",
    sampleType: "Sangre",
    preparationInstructions: "",
    turnaroundTime: {
      value: 24,
      unit: "horas",
    },
    price: {
      amount: 0,
      currency: "EUR",
    },
    metadata: {
      requiresFasting: false,
      requiresSpecialHandling: false,
      isPediatric: false,
      priority: "routine",
    },
    parameters: [],
  });

  const [parameters, setParameters] = useState([]);
  const [currentParameter, setCurrentParameter] = useState({
    name: "",
    unit: "",
    loincCode: "",
    referenceRange: {
      min: "",
      max: "",
      normal: "",
      note: "",
    },
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Estado para LOINC seleccionado (prueba principal)
  const [selectedTestLoinc, setSelectedTestLoinc] = useState(null);
  
  // Estado para LOINC seleccionado (parámetro)
  const [selectedParamLoinc, setSelectedParamLoinc] = useState(null);

  // Opciones estáticas
  const sampleTypesOptions = [
    { value: "Sangre", label: "Sangre" },
    { value: "Orina", label: "Orina" },
    { value: "Heces", label: "Heces" },
    { value: "Saliva", label: "Saliva" },
    { value: "Tejido", label: "Tejido" },
    { value: "Otro", label: "Otro" },
  ];

  const timeUnitsOptions = [
    { value: "minutos", label: "Minutos" },
    { value: "horas", label: "Horas" },
    { value: "días", label: "Días" },
  ];

  const prioritiesOptions = [
    { value: "routine", label: "ROUTINE" },
    { value: "urgent", label: "URGENT" },
    { value: "stat", label: "STAT" },
  ];

  const currenciesOptions = [
    { value: "EUR", label: "EUR" },
    { value: "USD", label: "USD" },
    { value: "MXN", label: "MXN" },
  ];

  const [categoryOptions, setCategoryOptions] = useState([]);

  // ========================================================================
  // FUNCIONES DE BÚSQUEDA LOINC
  // ========================================================================

  /**
   * Buscar códigos LOINC para la prueba principal
   * Se activa al escribir 3+ caracteres
   */
  const loadTestLoincOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return [];
    }

    try {
      const response = await searchLoincCodes({
        query: inputValue,
        limit: 15,
      });

      if (response.success && response.data) {
        return response.data.map((loinc) => ({
          value: loinc.loincNum,
          label: `${loinc.loincNum} - ${loinc.component}`,
          loincData: loinc, // Guardar datos completos
        }));
      }
      return [];
    } catch (error) {
      console.error("Error buscando códigos LOINC:", error);
      return [];
    }
  };

  /**
   * Buscar códigos LOINC para parámetros
   */
  const loadParamLoincOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return [];
    }

    try {
      const response = await searchLoincCodes({
        query: inputValue,
        limit: 20,
      });

      if (response.success && response.data) {
        return response.data.map((loinc) => ({
          value: loinc.loincNum,
          label: `${loinc.loincNum} - ${loinc.component}`,
          unit: loinc.property || "",
          loincData: loinc,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error buscando códigos LOINC para parámetros:", error);
      return [];
    }
  };

  /**
   * Al seleccionar un código LOINC para la prueba, autocompletar nombre
   */
  const handleTestLoincSelect = (selectedOption) => {
    setSelectedTestLoinc(selectedOption);
    
    if (selectedOption && selectedOption.loincData) {
      const loinc = selectedOption.loincData;
      
      setFormData((prev) => ({
        ...prev,
        loincCode: loinc.loincNum,
        name: loinc.longCommonName || loinc.component || prev.name,
        description: loinc.relatedNames || prev.description,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        loincCode: "",
      }));
    }
  };

  /**
   * Al escribir manualmente el código LOINC, buscar y autocompletar
   */
  const handleLoincCodeBlur = async (e) => {
    const code = e.target.value.trim();
    
    if (!code) return;

    try {
      const response = await getLoincCodeByNumber(code);
      
      if (response.success && response.data) {
        const loinc = response.data;
        
        setFormData((prev) => ({
          ...prev,
          name: loinc.longCommonName || loinc.component || prev.name,
          description: loinc.relatedNames || prev.description,
        }));

        setSelectedTestLoinc({
          value: loinc.loincNum,
          label: `${loinc.loincNum} - ${loinc.component}`,
          loincData: loinc,
        });
      }
    } catch (error) {
      // Si no encuentra, permitir código manual
      console.log("Código LOINC no encontrado, permitiendo código manual");
    }
  };

  /**
   * Al seleccionar un LOINC para parámetro, autocompletar datos
   */
  const handleParamLoincSelect = (selectedOption) => {
    setSelectedParamLoinc(selectedOption);
    
    if (selectedOption && selectedOption.loincData) {
      const loinc = selectedOption.loincData;
      
      setCurrentParameter((prev) => ({
        ...prev,
        loincCode: loinc.loincNum,
        name: loinc.component || prev.name,
        unit: loinc.property || prev.unit,
        description: loinc.longCommonName || prev.description,
      }));
    } else {
      setCurrentParameter((prev) => ({
        ...prev,
        loincCode: "",
      }));
    }
  };

  /**
   * Al escribir manualmente el código LOINC del parámetro
   */
  const handleParamLoincCodeBlur = async (e) => {
    const code = e.target.value.trim();
    
    if (!code) return;

    try {
      const response = await getLoincCodeByNumber(code);
      
      if (response.success && response.data) {
        const loinc = response.data;
        
        setCurrentParameter((prev) => ({
          ...prev,
          name: loinc.component || prev.name,
          unit: loinc.property || prev.unit,
          description: loinc.longCommonName || prev.description,
        }));

        setSelectedParamLoinc({
          value: loinc.loincNum,
          label: `${loinc.loincNum} - ${loinc.component}`,
          loincData: loinc,
        });
      }
    } catch (error) {
      console.log("Código LOINC de parámetro no encontrado");
    }
  };

  // ========================================================================
  // CARGAR DATOS EN MODO EDICIÓN
  // ========================================================================

  useEffect(() => {
    if (isEditMode && templateData) {
      setFormData({
        name: templateData.name || "",
        code: templateData.code || "",
        loincCode: templateData.loincCode || "",
        category: templateData.category || "",
        description: templateData.description || "",
        sampleType: templateData.sampleType || "Sangre",
        preparationInstructions: templateData.preparationInstructions || "",
        turnaroundTime: templateData.turnaroundTime || { value: 24, unit: "horas" },
        price: templateData.price || { amount: 0, currency: "EUR" },
        metadata: templateData.metadata || {
          requiresFasting: false,
          requiresSpecialHandling: false,
          isPediatric: false,
          priority: "routine",
        },
        parameters: templateData.parameters || [],
      });
      setParameters(templateData.parameters || []);
    }
  }, [isEditMode, templateData]);

  // Cargar categorías
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getLabTemplateCategories();
      if (response.success) {
        setCategories(response.data);
        const options = response.data.map(cat => ({ value: cat, label: cat }));
        const defaultCategories = [
          { value: "Hematología", label: "Hematología" },
          { value: "Bioquímica", label: "Bioquímica" },
          { value: "Uroanálisis", label: "Uroanálisis" },
          { value: "Coprología", label: "Coprología" },
          { value: "Endocrinología", label: "Endocrinología" },
          { value: "Inmunología", label: "Inmunología" },
          { value: "Microbiología", label: "Microbiología" },
        ];
        
        const allOptions = [...options, ...defaultCategories];
        const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.value, item])).values());
        setCategoryOptions(uniqueOptions);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategoryOptions([
        { value: "Hematología", label: "Hematología" },
        { value: "Bioquímica", label: "Bioquímica" },
        { value: "Uroanálisis", label: "Uroanálisis" },
        { value: "Coprología", label: "Coprología" },
        { value: "Endocrinología", label: "Endocrinología" },
        { value: "Inmunología", label: "Inmunología" },
        { value: "Microbiología", label: "Microbiología" },
      ]);
    }
  };

  // ========================================================================
  // HANDLERS DE FORMULARIO
  // ========================================================================

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption ? selectedOption.value : "";
    
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleParameterChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes("referenceRange.")) {
      const field = name.split(".")[1];
      setCurrentParameter((prev) => ({
        ...prev,
        referenceRange: {
          ...prev.referenceRange,
          [field]: value,
        },
      }));
    } else {
      setCurrentParameter((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddParameter = () => {
    if (!currentParameter.name.trim()) {
      alert("Parameter name is required");
      return;
    }

    const newParameter = {
      ...currentParameter,
      referenceRange: {
        min: currentParameter.referenceRange.min ? Number(currentParameter.referenceRange.min) : undefined,
        max: currentParameter.referenceRange.max ? Number(currentParameter.referenceRange.max) : undefined,
        normal: currentParameter.referenceRange.normal || undefined,
        note: currentParameter.referenceRange.note || undefined,
      },
    };

    setParameters([...parameters, newParameter]);
    
    // Limpiar formulario
    setCurrentParameter({
      name: "",
      unit: "",
      loincCode: "",
      referenceRange: {
        min: "",
        max: "",
        normal: "",
        note: "",
      },
      description: "",
    });
    setSelectedParamLoinc(null);
  };

  const handleRemoveParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.code.trim()) newErrors.code = "Code is required";
    if (!formData.loincCode.trim()) newErrors.loincCode = "LOINC Code is required";
    if (!formData.category.trim()) newErrors.category = "Category is required";
    if (!formData.sampleType) newErrors.sampleType = "Sample Type is required";
    if (parameters.length === 0) newErrors.parameters = "At least one parameter is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        parameters: parameters,
        turnaroundTime: {
          value: Number(formData.turnaroundTime.value),
          unit: formData.turnaroundTime.unit,
        },
        price: {
          amount: Number(formData.price.amount),
          currency: formData.price.currency,
        },
      };

      if (isEditMode) {
        await updateLabTemplate(templateData._id, submitData);
        setShowSuccess(true);
        setTimeout(() => {
          navigate("/laboratoryTemplates");
        }, 1500);
      } else {
        await createLabTemplate(submitData);
        setShowSuccess(true);
        setTimeout(() => {
          navigate("/laboratoryTemplates");
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Error saving laboratory template"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/laboratoryTemplates");
  };

  const getSelectedOption = (options, value) => {
    return options.find(option => option.value === value) || null;
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#80bdff' : errors[state.selectProps.name] ? '#dc3545' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#80bdff' : '#ced4da',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid px-4 py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold">
            {isEditMode ? "Edit Laboratory Template" : "Add Laboratory Template"}
          </h3>
          <Button variant="secondary" onClick={handleCancel}>
            Back to List
          </Button>
        </div>

        {showSuccess && (
          <Alert variant="success" dismissible onClose={() => setShowSuccess(false)}>
            Laboratory template {isEditMode ? "updated" : "created"} successfully!
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Basic Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Test Name / Search LOINC <span className="text-danger">*</span>
                    </Form.Label>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadTestLoincOptions}
                      onChange={handleTestLoincSelect}
                      value={selectedTestLoinc}
                      placeholder="Escribe 3+ letras para buscar en LOINC..."
                      isClearable
                      styles={customSelectStyles}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 3
                          ? "Escribe al menos 3 caracteres"
                          : "No se encontraron códigos LOINC"
                      }
                    />
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      isInvalid={!!errors.name}
                      placeholder="O escribe el nombre manualmente"
                      className="mt-2"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                    {selectedTestLoinc && (
                      <Badge bg="success" className="mt-1">
                        LOINC: {selectedTestLoinc.value}
                      </Badge>
                    )}
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      isInvalid={!!errors.code}
                      placeholder="e.g., HMG001"
                      disabled={isEditMode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.code}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      LOINC Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="loincCode"
                      value={formData.loincCode}
                      onChange={handleInputChange}
                      onBlur={handleLoincCodeBlur}
                      isInvalid={!!errors.loincCode}
                      placeholder="e.g., 57021-8"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.loincCode}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      O selecciona desde búsqueda arriba
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Category <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      name="category"
                      options={categoryOptions}
                      value={getSelectedOption(categoryOptions, formData.category)}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Buscar o seleccionar categoría..."
                      isClearable
                      isSearchable
                    />
                    {errors.category && (
                      <div className="text-danger small mt-1">{errors.category}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Sample Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      name="sampleType"
                      options={sampleTypesOptions}
                      value={getSelectedOption(sampleTypesOptions, formData.sampleType)}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Buscar o seleccionar tipo de muestra..."
                      isClearable
                      isSearchable
                    />
                    {errors.sampleType && (
                      <div className="text-danger small mt-1">{errors.sampleType}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of the test..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Preparation Instructions</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="preparationInstructions"
                      value={formData.preparationInstructions}
                      onChange={handleInputChange}
                      placeholder="e.g., Ayuno de 8-12 horas..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Test Details */}
          <Card className="mb-4">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">Test Details</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Turnaround Time</Form.Label>
                    <Form.Control
                      type="number"
                      name="turnaroundTime.value"
                      value={formData.turnaroundTime.value}
                      onChange={handleInputChange}
                      min="1"
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Time Unit</Form.Label>
                    <Select
                      name="turnaroundTime.unit"
                      options={timeUnitsOptions}
                      value={getSelectedOption(timeUnitsOptions, formData.turnaroundTime.unit)}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Seleccionar unidad..."
                      isSearchable
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price</Form.Label>
                    <Form.Control
                      type="number"
                      name="price.amount"
                      value={formData.price.amount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Select
                      name="price.currency"
                      options={currenciesOptions}
                      value={getSelectedOption(currenciesOptions, formData.price.currency)}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Seleccionar moneda..."
                      isSearchable
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Metadata */}
          <Card className="mb-4">
            <Card.Header className="bg-warning">
              <h5 className="mb-0">Metadata</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Select
                      name="metadata.priority"
                      options={prioritiesOptions}
                      value={getSelectedOption(prioritiesOptions, formData.metadata.priority)}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Seleccionar prioridad..."
                      isSearchable
                    />
                  </Form.Group>
                </Col>

                <Col md={9}>
                  <div className="d-flex gap-4 mt-4">
                    <Form.Check
                      type="checkbox"
                      label="Requires Fasting"
                      name="metadata.requiresFasting"
                      checked={formData.metadata.requiresFasting}
                      onChange={handleInputChange}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Requires Special Handling"
                      name="metadata.requiresSpecialHandling"
                      checked={formData.metadata.requiresSpecialHandling}
                      onChange={handleInputChange}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Pediatric"
                      name="metadata.isPediatric"
                      checked={formData.metadata.isPediatric}
                      onChange={handleInputChange}
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Parameters Section */}
          <Card className="mb-4">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                Parameters <span className="text-danger">*</span>
              </h5>
            </Card.Header>
            <Card.Body>
              {errors.parameters && (
                <Alert variant="danger">{errors.parameters}</Alert>
              )}

              {/* Add Parameter Form */}
              <div className="border rounded p-3 mb-3 bg-light">
                <h6 className="mb-3">Add New Parameter</h6>
                
                {/* Búsqueda LOINC para parámetro */}
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Search Parameter in LOINC</Form.Label>
                      <AsyncSelect
                        cacheOptions
                        loadOptions={loadParamLoincOptions}
                        onChange={handleParamLoincSelect}
                        value={selectedParamLoinc}
                        placeholder="Escribe 3+ letras para buscar parámetro en LOINC..."
                        isClearable
                        styles={customSelectStyles}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.length < 3
                            ? "Escribe al menos 3 caracteres"
                            : "No se encontraron parámetros LOINC"
                        }
                      />
                      {selectedParamLoinc && (
                        <Badge bg="info" className="mt-1">
                          LOINC: {selectedParamLoinc.value}
                        </Badge>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Parameter Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={currentParameter.name}
                        onChange={handleParameterChange}
                        placeholder="e.g., Hemoglobina"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit</Form.Label>
                      <Form.Control
                        type="text"
                        name="unit"
                        value={currentParameter.unit}
                        onChange={handleParameterChange}
                        placeholder="e.g., g/dL"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>LOINC Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="loincCode"
                        value={currentParameter.loincCode}
                        onChange={handleParameterChange}
                        onBlur={handleParamLoincCodeBlur}
                        placeholder="e.g., 718-7"
                      />
                      <Form.Text className="text-muted">
                        O busca arriba
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Min</Form.Label>
                      <Form.Control
                        type="number"
                        name="referenceRange.min"
                        value={currentParameter.referenceRange.min}
                        onChange={handleParameterChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max</Form.Label>
                      <Form.Control
                        type="number"
                        name="referenceRange.max"
                        value={currentParameter.referenceRange.max}
                        onChange={handleParameterChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      onClick={handleAddParameter}
                      className="mb-3 w-100"
                    >
                      Add
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Normal Value (Text)</Form.Label>
                      <Form.Control
                        type="text"
                        name="referenceRange.normal"
                        value={currentParameter.referenceRange.normal}
                        onChange={handleParameterChange}
                        placeholder="e.g., Negativo, Amarillo..."
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        name="referenceRange.note"
                        value={currentParameter.referenceRange.note}
                        onChange={handleParameterChange}
                        placeholder="Additional notes..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Parameters List */}
              {parameters.length > 0 && (
                <div>
                  <h6 className="mb-3">Added Parameters ({parameters.length})</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Unit</th>
                          <th>LOINC</th>
                          <th>Min</th>
                          <th>Max</th>
                          <th>Normal</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, index) => (
                          <tr key={index}>
                            <td>{param.name}</td>
                            <td>{param.unit || "-"}</td>
                            <td>
                              {param.loincCode ? (
                                <Badge bg="secondary">{param.loincCode}</Badge>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{param.referenceRange?.min || "-"}</td>
                            <td>{param.referenceRange?.max || "-"}</td>
                            <td>{param.referenceRange?.normal || "-"}</td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleRemoveParameter(index)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update Template" : "Create Template"}
            </Button>
          </div>
        </Form>
      </div>
    </main>
  );
};

export default LaboratoryTemplateAdd;